import React, { useState, useCallback, useRef, useEffect } from "react";
import { Camera, Upload, Clipboard, X, Image as ImageIcon } from "lucide-react";
import html2canvas from "html2canvas";

/**
 * Screenshot uploader with file upload, clipboard paste, and in-browser capture.
 * Props: uid, onScreenshotsChange, required
 */
export default function ScreenshotUploader({ onScreenshotsChange, required = false }) {
  const [previews, setPreviews] = useState([]); // { file, previewUrl }
  const [capturing, setCapturing] = useState(false);
  const fileInputRef = useRef(null);

  // Notify parent of changes
  useEffect(() => {
    onScreenshotsChange(previews.map((p) => p.file));
  }, [previews, onScreenshotsChange]);

  // File upload handler
  const handleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    const newPreviews = files
      .filter((f) => f.type.startsWith("image/"))
      .map((file) => ({
        file,
        previewUrl: URL.createObjectURL(file),
      }));
    setPreviews((prev) => [...prev, ...newPreviews]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  // Clipboard paste handler
  const handlePaste = useCallback(async () => {
    try {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        const imageType = item.types.find((t) => t.startsWith("image/"));
        if (imageType) {
          const blob = await item.getType(imageType);
          const file = new File([blob], `clipboard-${Date.now()}.png`, { type: imageType });
          setPreviews((prev) => [
            ...prev,
            { file, previewUrl: URL.createObjectURL(file) },
          ]);
        }
      }
    } catch {
      // Fallback: listen for paste event
    }
  }, []);

  // In-browser capture of <main>
  const handleCapture = useCallback(async () => {
    setCapturing(true);
    try {
      const mainEl = document.querySelector("main");
      if (!mainEl) return;
      const isDark = document.documentElement.classList.contains("dark");
      const canvas = await html2canvas(mainEl, {
        useCORS: true,
        backgroundColor: isDark ? "#050816" : "#ffffff",
      });
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
      if (blob) {
        const file = new File([blob], `page-capture-${Date.now()}.png`, { type: "image/png" });
        setPreviews((prev) => [
          ...prev,
          { file, previewUrl: URL.createObjectURL(file) },
        ]);
      }
    } catch (err) {
      console.error("Page capture failed:", err);
    } finally {
      setCapturing(false);
    }
  }, []);

  // Remove a preview
  const handleRemove = useCallback((index) => {
    setPreviews((prev) => {
      const removed = prev[index];
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  // Listen for paste events on the document
  useEffect(() => {
    const onPaste = (e) => {
      const items = Array.from(e.clipboardData?.items || []);
      items.forEach((item) => {
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            setPreviews((prev) => [
              ...prev,
              { file, previewUrl: URL.createObjectURL(file) },
            ]);
          }
        }
      });
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, []);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <ImageIcon size={14} className="text-cyan-400" />
        <span className="text-xs font-bold text-slate-300">
          Screenshots {required && <span className="text-red-400">*</span>}
        </span>
      </div>

      {/* Upload Methods */}
      <div className="flex gap-2 flex-wrap">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 text-xs font-medium hover:border-cyan-500/50 transition-colors"
        >
          <Upload size={12} /> Upload
        </button>
        <button
          type="button"
          onClick={handlePaste}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 text-xs font-medium hover:border-cyan-500/50 transition-colors"
        >
          <Clipboard size={12} /> Paste
        </button>
        <button
          type="button"
          onClick={handleCapture}
          disabled={capturing}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 text-xs font-medium hover:border-cyan-500/50 transition-colors disabled:opacity-50"
        >
          <Camera size={12} /> {capturing ? "Capturing..." : "Capture Page"}
        </button>
      </div>

      {/* Previews */}
      {previews.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {previews.map((p, i) => (
            <div key={i} className="relative group">
              <img
                src={p.previewUrl}
                alt={`Screenshot ${i + 1}`}
                className="w-16 h-16 object-cover rounded-lg border border-slate-700"
              />
              <button
                type="button"
                onClick={() => handleRemove(i)}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X size={10} />
              </button>
            </div>
          ))}
        </div>
      )}

      {required && previews.length === 0 && (
        <p className="text-[10px] text-red-400">At least one screenshot is required for bug reports</p>
      )}
    </div>
  );
}
